import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PageTitle } from './';
import { cmsService, CMSCustomer } from '../services/cms';
import { useThemeMode } from '../contexts/ThemeContext';
import { resolveImageUrl, withImageFallback } from '../utils/imageFallback';

const OurCustomers: React.FC = () => {
  const { t } = useTranslation();
  const { mode } = useThemeMode();
  const [customers, setCustomers] = useState<CMSCustomer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomers();

    // Subscribe to real-time updates
    const subscription = cmsService.subscribeToChanges('cms_customers', () => {
      fetchCustomers();
    });

    return () => {
      cmsService.unsubscribe(subscription);
    };
  }, []);

  const fetchCustomers = async () => {
    try {
      const { data, error } = await cmsService.getCustomers(true); // Only get active customers
      if (!error && data) {
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Double the customers array for seamless loop
  const doubledCustomers = [...customers, ...customers];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (customers.length === 0) {
    return null; // Don't render the section if no customers
  }

  return (
    <Box sx={{
      my: { xs: 4, sm: 6, md: 8 },
      position: 'relative',
      overflow: 'hidden'
    }}>
      <PageTitle
        text={t('ourCustomers.title')}
        align="center"
        size="medium"
      />
      
      <Box
        sx={{
          position: 'relative',
          mt: 1,
          py: { xs: 0.5, sm: 1 },
          background: mode === 'light' ? 'oklch(98.7% 0.026 102.212)' : 'transparent',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: { xs: '40px', sm: '70px', md: '120px' },
            height: '100%',
            background: mode === 'light' ? 'oklch(98.7% 0.026 102.212 / 0.95)' : 'transparent',
            zIndex: 3,
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: { xs: '40px', sm: '70px', md: '120px' },
            height: '100%',
            background: mode === 'light' ? 'oklch(98.7% 0.026 102.212 / 0.95)' : 'transparent',
            zIndex: 3,
            pointerEvents: 'none',
          },
        }}
      >
        <Box
          className="customers-track"
          sx={{
            display: 'flex',
            width: 'max-content',
            animation: {
              xs: 'customersSlideLoop 25s linear infinite',
              sm: 'customersSlideLoop 35s linear infinite',
              md: 'customersSlideLoop 45s linear infinite'
            },
            '@keyframes customersSlideLoop': {
              '0%': {
                transform: 'translateX(0)',
              },
              '100%': {
                transform: 'translateX(calc(-100% / 2))',
              },
            },
          }}
        >
          {doubledCustomers.map((customer, index) => (
            <Box
              key={`${customer.id || index}-${index}`}
              sx={{
                flex: '0 0 auto',
                mx: { xs: 2, sm: 3, md: 4 },
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 1, sm: 1.5, md: 2 },
                // borderRadius: 3,
                // background: 'oklch(95% 0.026 102.212)',
                // border: '1px solid rgba(0,0,0,0.06)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                minWidth: { xs: 140, sm: 180, md: 240 },
                '&:hover': {
                  transform: { xs: 'none', sm: 'translateY(-6px)' },
                  // background: 'oklch(93% 0.026 102.212)',
                  // borderColor: 'rgba(0,0,0,0.12)',
                  // boxShadow: '0 15px 40px rgba(0,0,0,0.12)',
                  '& .customer-logo': {
                    transform: { xs: 'none', sm: 'scale(1.05)' },
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <Box
                  className="customer-logo"
                  sx={{
                    width: { xs: 110, sm: 140, md: 180 },
                    height: { xs: 45, sm: 55, md: 70 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    mb: { xs: 0.5, sm: 0.75, md: 1 },
                  }}
                >
                  <Box
                    component="img"
                    src={resolveImageUrl(customer.logo_url, '/placeholder-product.png')}
                    alt={customer.name || `Customer ${index + 1}`}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                    onError={(e) => withImageFallback(e, '/placeholder-product.png')}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

     
    </Box>
  );
};

export default OurCustomers;
